import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Loader2, Clock, CalendarIcon, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { supabase } from '@/integrations/supabase/client';
import { getAvailableSlots, TimeSlot } from '@/lib/availabilityChecker';
import { BUSINESS_TIMEZONE, TIMEZONE_LABEL } from '@/lib/timezoneConfig';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface RescheduleAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: any;
  onReschedule: () => void;
}

export default function RescheduleAppointmentDialog({
  open,
  onOpenChange,
  appointment,
  onReschedule,
}: RescheduleAppointmentDialogProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingSlots, setFetchingSlots] = useState(false);
  const [notes, setNotes] = useState('');
  const [sendEmailNotification, setSendEmailNotification] = useState(true);
  const { toast } = useToast();

  // Reset state when dialog opens/closes or appointment changes
  useEffect(() => {
    if (open && appointment) {
      // Set initial date to current appointment date or today
      const initialDate = appointment.preferred_date 
        ? new Date(appointment.preferred_date) 
        : new Date();
      setSelectedDate(initialDate);
      setSelectedTimeSlot(null);
      setNotes('');
      setSendEmailNotification(true);
    } else {
      // Reset all state when dialog closes
      setSelectedDate(undefined);
      setSelectedTimeSlot(null);
      setAvailableSlots([]);
      setNotes('');
      setSendEmailNotification(true);
    }
  }, [open, appointment]);

  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async () => {
    if (!selectedDate) return;

    setFetchingSlots(true);
    try {
      const slots = await getAvailableSlots(selectedDate);
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast({
        title: 'Error',
        description: 'Unable to load time slots. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setFetchingSlots(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTimeSlot || !appointment) {
      toast({
        title: 'Missing Information',
        description: 'Please select both a date and time slot.',
        variant: 'destructive',
      });
      return;
    }

    // Double-check slot is still available
    if (!selectedTimeSlot.isAvailable) {
      toast({
        title: 'Slot Unavailable',
        description: 'This time slot is no longer available. Please select another.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const duration = appointment.duration_minutes || 60;
      const scheduledStart = selectedTimeSlot.startTime;
      const scheduledEnd = new Date(scheduledStart.getTime() + duration * 60000);

      // Update appointment
      const newNotes = appointment.notes 
        ? `${appointment.notes}\n\n[${format(new Date(), 'PPpp')}] Rescheduled by admin: ${notes || 'No reason provided'}`
        : `[${format(new Date(), 'PPpp')}] Rescheduled by admin: ${notes || 'No reason provided'}`;

      const { error: updateError } = await supabase
        .from('appointments')
        .update({
          preferred_date: format(selectedDate, 'yyyy-MM-dd'),
          preferred_time: format(scheduledStart, 'h:mm a'),
          scheduled_start_time: scheduledStart.toISOString(),
          scheduled_end_time: scheduledEnd.toISOString(),
          notes: newNotes,
        })
        .eq('id', appointment.id);

      if (updateError) throw updateError;

      // Send email notification if enabled
      if (sendEmailNotification) {
        try {
          // Get client details
          const { data: clientData } = await supabase
            .from('clients')
            .select('full_name, email')
            .eq('id', appointment.client_id)
            .single();

          // Get email template ID for appointment_approved
          const { data: templateData } = await supabase
            .from('email_templates')
            .select('id')
            .eq('template_name', 'appointment_approved')
            .eq('is_active', true)
            .single();

          if (templateData) {
            await supabase.functions.invoke('send-email', {
              body: {
                template_id: templateData.id,
                client_id: appointment.client_id,
                recipient_email: appointment.email,
                variables: {
                  client_name: clientData?.full_name || appointment.name,
                  service_type: appointment.service_type,
                  appointment_date: format(selectedDate, 'MMMM d, yyyy'),
                  appointment_time: `${format(scheduledStart, 'h:mm a')} ${TIMEZONE_LABEL}`,
                  duration: duration.toString(),
                  meeting_link: appointment.appointment_type === 'virtual' 
                    ? 'Virtual meeting link will be sent separately' 
                    : 'In-person at Lenexa, KS office',
                },
                related_appointment_id: appointment.id,
                subject: '✨ Your Appointment Has Been Rescheduled',
              },
            });
          }
        } catch (emailError) {
          console.error('Email error:', emailError);
          // Don't fail the whole operation if email fails
          toast({
            title: 'Appointment Rescheduled',
            description: 'Appointment updated, but email notification failed. Please contact the client directly.',
            variant: 'default',
          });
        }
      }

      toast({
        title: 'Appointment Rescheduled',
        description: `Moved to ${format(selectedDate, 'MMM d')} at ${format(scheduledStart, 'h:mm a')}${sendEmailNotification ? '. Email sent to client.' : ''}`,
      });

      onReschedule();
      onOpenChange(false);
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      toast({
        title: 'Error',
        description: 'Failed to reschedule appointment. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if this is the current appointment time
  const isCurrentAppointmentTime = (slot: TimeSlot) => {
    if (!appointment.scheduled_start_time || !selectedDate) return false;
    try {
      const currentStart = new Date(appointment.scheduled_start_time);
      const currentDate = new Date(appointment.preferred_date);
      return (
        format(currentDate, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd') &&
        format(currentStart, 'HH:mm') === format(slot.startTime, 'HH:mm')
      );
    } catch {
      return false;
    }
  };

  // Show different timezone if client has one
  const clientTimezone = appointment?.preferred_timezone;
  const showTimezoneConversion = clientTimezone && clientTimezone !== BUSINESS_TIMEZONE;

  const formatTimeInClientTimezone = (date: Date) => {
    if (!showTimezoneConversion) return null;
    try {
      const clientTime = toZonedTime(date, clientTimezone);
      return format(clientTime, 'h:mm a');
    } catch {
      return null;
    }
  };

  if (!appointment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reschedule Appointment</DialogTitle>
          <DialogDescription>
            Select a new date and time for this appointment
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Appointment Details */}
          <Card className="p-4 bg-muted/50">
            <h3 className="font-semibold mb-2">Current Appointment</h3>
            <div className="space-y-1 text-sm">
              <p><strong>Client:</strong> {appointment.name}</p>
              <p><strong>Service:</strong> {appointment.service_type}</p>
              <p>
                <strong>Current Time:</strong>{' '}
                {appointment.preferred_date && appointment.preferred_time
                  ? `${format(new Date(appointment.preferred_date), 'MMMM d, yyyy')} at ${appointment.preferred_time}`
                  : 'Not scheduled yet'}
                {appointment.duration_minutes && ` (${appointment.duration_minutes} min)`}
              </p>
              {showTimezoneConversion && (
                <Badge variant="secondary" className="mt-2">
                  Client Timezone: {clientTimezone}
                </Badge>
              )}
            </div>
          </Card>

          {/* Date Selection */}
          <div className="space-y-2">
            <Label>Select New Date</Label>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return date < today;
                }}
                className="rounded-md border pointer-events-auto"
              />
            </div>
          </div>

          {/* Time Slot Selection */}
          {selectedDate && (
            <div className="space-y-2">
              <Label>Select New Time Slot</Label>
              {fetchingSlots ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading available slots...</span>
                </div>
              ) : availableSlots.length === 0 ? (
                <Card className="p-8 text-center border-dashed">
                  <p className="text-sm text-muted-foreground">
                    No available time slots for this date. Please choose another day.
                  </p>
                </Card>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-2 border rounded-md">
                  {availableSlots.map((slot, index) => {
                    const isCurrent = isCurrentAppointmentTime(slot);
                    const isSelected = selectedTimeSlot?.startTime.getTime() === slot.startTime.getTime();
                    const clientTime = formatTimeInClientTimezone(slot.startTime);

                    return (
                      <Button
                        key={index}
                        variant={isSelected ? 'default' : 'outline'}
                        size="sm"
                        disabled={!slot.isAvailable}
                        onClick={() => setSelectedTimeSlot(slot)}
                        className={cn(
                          'h-auto flex-col items-start p-2 text-xs',
                          isCurrent && 'border-yellow-500 border-2',
                          slot.isAfterHours && slot.isAvailable && 'border-orange-400 bg-orange-50 hover:bg-orange-100',
                          isSelected && 'border-green-500 bg-green-500 text-white'
                        )}
                      >
                        <span className="font-semibold">{format(slot.startTime, 'h:mm a')}</span>
                        <span className="text-[10px] opacity-80">
                          {appointment.duration_minutes || 60} min
                        </span>
                        {isCurrent && (
                          <Badge variant="secondary" className="text-[8px] mt-1">
                            Current
                          </Badge>
                        )}
                        {slot.isAfterHours && slot.isAvailable && (
                          <Badge variant="secondary" className="text-[8px] mt-1 bg-orange-100">
                            After Hours
                          </Badge>
                        )}
                        {!slot.isAvailable && (
                          <Badge variant="secondary" className="text-[8px] mt-1">
                            Booked
                          </Badge>
                        )}
                        {clientTime && isSelected && (
                          <span className="text-[9px] opacity-70 mt-1">
                            {clientTime} (Client)
                          </span>
                        )}
                      </Button>
                    );
                  })}
                </div>
              )}

              {/* Show selected time with timezone conversion */}
              {selectedTimeSlot && showTimezoneConversion && (
                <Card className="p-3 bg-blue-50 border-blue-200 mt-2">
                  <p className="text-sm">
                    <strong>Selected Time:</strong>
                    <br />
                    {format(selectedTimeSlot.startTime, 'h:mm a')} {TIMEZONE_LABEL} = {formatTimeInClientTimezone(selectedTimeSlot.startTime)} ({clientTimezone})
                  </p>
                </Card>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Reschedule Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add a note about why this appointment is being rescheduled (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Email Notification */}
          <div className="flex items-start space-x-2">
            <Checkbox
              id="send-email"
              checked={sendEmailNotification}
              onCheckedChange={(checked) => setSendEmailNotification(checked as boolean)}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="send-email"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Send email notification to client
              </Label>
              {sendEmailNotification && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  Client will receive confirmation at: {appointment.email}
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!selectedDate || !selectedTimeSlot || loading}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Rescheduling...
              </>
            ) : (
              <>
                <Clock className="h-4 w-4 mr-2" />
                Confirm Reschedule
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
